import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { Eyebrow, FadeUp } from "@/components/Reveal";

export default function Professionals() {
  const { t, localizePath } = useLang();
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const items = t("pro.items");

  return (
    <section id="professionals" ref={ref} data-testid="for-professionals" className="bg-ink text-paper">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 md:grid-cols-2">
        <div className="flex flex-col justify-center px-5 py-20 md:px-14 md:py-32">
          <FadeUp><Eyebrow dark>{t("pro.eyebrow")}</Eyebrow></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-8 font-serif text-4xl font-medium leading-[1.08] tracking-tight md:text-5xl">
              {t("pro.title")}
            </h2>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-6 max-w-md text-base leading-relaxed text-paper/60">{t("pro.body")}</p>
          </FadeUp>

          <ul className="mt-12">
            {items.map((item, i) => (
              <FadeUp key={item} delay={0.25 + i * 0.08} amount={0.6}>
                <li
                  data-testid={`pro-item-${i}`}
                  className="flex items-center gap-5 border-t border-paper/15 py-4 last:border-b"
                >
                  <span className="font-sans text-sm font-light text-cobalt">0{i + 1}</span>
                  <span className="text-base tracking-tight md:text-lg">{item}</span>
                </li>
              </FadeUp>
            ))}
          </ul>

          <FadeUp delay={0.5} className="mt-12">
            <Link
              to={localizePath("/enterprise")}
              data-testid="professionals-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-cobalt px-7 py-3.5 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:bg-paper hover:text-ink active:scale-[0.98]"
            >
              {t("pro.cta")}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </FadeUp>
        </div>

        <div className="relative min-h-[340px] overflow-hidden md:min-h-[640px]">
          <motion.img
            src="/images/clinical-professionals.jpg"
            alt="A clinician reviewing information on a tablet with a patient"
            data-testid="professionals-image"
            loading="lazy"
            style={reduce ? {} : { y }}
            className="absolute inset-0 h-[116%] w-full object-cover"
          />
          <div className="absolute inset-0 bg-ink/20" aria-hidden="true" />
          <svg viewBox="0 0 100 100" className="absolute bottom-6 left-6 h-16 w-16" aria-hidden="true">
            <path d="M0 30 V0 H30" fill="none" stroke="#0047FF" strokeWidth="3" />
            <path d="M70 100 H100 V70" fill="none" stroke="#F9F9F7" strokeOpacity="0.6" strokeWidth="3" />
          </svg>
        </div>
      </div>
    </section>
  );
}
